<script setup lang="ts">
import WarehouseIcon from '../icons/WarehouseIcon.vue'
import { clearAuthToken } from '../../services/authToken'
import router from '../../router'
import BackIcon from '../icons/BackIcon.vue'
import { useRoute } from 'vue-router'
import { computed, ComputedRef, ref } from 'vue'
import { updateOrderStatus } from '../../services/orderApi'
import { Modal } from 'bootstrap'
import { clearApiUrl } from '../../store/apiUrlStore'

const canLogout: ComputedRef<boolean> = computed(() => !route.meta.noLogout)
async function goBack() {
  if (!canLogout.value) {
    try {
      await updateOrderStatus(route.params.id as string, 'pending')
    } catch (error) {
      console.error(error)
    }
  }
  if (route.name === 'order-processing' && route.params.id) {
    await router.push({ name: 'order', params: { id: route.params.id } })
    return
  }
  if (route.name === 'order') {
    await router.push({ name: 'dashboard' })
    return
  }
  if (window.history.length > 1) {
    router.back()
    return
  }

  await router.replace({ name: 'dashboard' })
}

async function logout() {
  if (modalInstance) {
    modalInstance.hide()
  }

  if (resetApiUrlModalInstance) {
    resetApiUrlModalInstance.hide()
  }

  await new Promise((resolve) => setTimeout(resolve, 300))

  await clearAuthToken()
  await router.push({ name: 'login' })
}

const route = useRoute()
const pageTitle: ComputedRef<string> = computed(() => (route.meta.title as string) ?? '')

const modalEl = ref<HTMLElement | null>(null)
let modalInstance: Modal | null = null

function openModal() {
  if (!modalEl.value) return

  if (!modalInstance) {
    modalInstance = new Modal(modalEl.value, {
      backdrop: true,
      keyboard: true,
    })
  }

  modalInstance.show()
}

const resetApiUrlModalEl = ref<HTMLElement | null>(null)
let resetApiUrlModalInstance: Modal | null = null

let logoClickCount = 0
let logoClickTimer: ReturnType<typeof setTimeout> | null = null

const REQUIRED_LOGO_CLICKS = 10
const LOGO_CLICK_WINDOW_MS = 4000

function openResetApiUrlModal() {
  if (!resetApiUrlModalEl.value) return

  if (!resetApiUrlModalInstance) {
    resetApiUrlModalInstance = new Modal(resetApiUrlModalEl.value, {
      backdrop: true,
      keyboard: true,
    })
  }

  resetApiUrlModalInstance.show()
}

function onLogoClick() {
  logoClickCount += 1

  if (logoClickTimer) {
    clearTimeout(logoClickTimer)
  }

  logoClickTimer = setTimeout(() => {
    logoClickCount = 0
    logoClickTimer = null
  }, LOGO_CLICK_WINDOW_MS)

  if (logoClickCount < REQUIRED_LOGO_CLICKS) {
    return
  }

  logoClickCount = 0

  if (logoClickTimer) {
    clearTimeout(logoClickTimer)
    logoClickTimer = null
  }

  openResetApiUrlModal()
}

async function resetApiUrl() {
  if (resetApiUrlModalInstance) {
    resetApiUrlModalInstance.hide()
  }

  await new Promise((resolve) => setTimeout(resolve, 300))

  await clearApiUrl()
  await logout()
}
</script>

<template>
  <div id="pageTop"></div>
  <header class="header">
    <div class="container">
      <div class="header-inner">
        <button v-if="route.name !== 'dashboard'" class="header-logo" @click="goBack">
          <BackIcon />
          <span v-if="pageTitle">{{ $t(pageTitle) }}</span>
        </button>
        <button v-else class="header-logo" @click="onLogoClick">
          <WarehouseIcon />
          <span>Opero</span>
        </button>
        <button v-if="canLogout" class="header-logout" @click="openModal">
          {{ $t('logout') }}
        </button>
        <span v-else />
      </div>
    </div>
  </header>
  <div v-if="canLogout" ref="modalEl" class="modal fade" tabindex="-1" aria-hidden="true">
    <div class="modal-dialog modal-dialog-centered">
      <div class="modal-content">
        <div class="modal-body">
          <h4 class="mb-16">{{ $t('logout-header') }}</h4>
          <p class="mb-32">{{ $t('logout-text') }}</p>
          <div class="d-flex flex-wrap gap-12">
            <button type="button" class="btn btn-outline-secondary flex-1" data-bs-dismiss="modal">
              {{ $t('cancel') }}
            </button>
            <button type="button" class="btn btn-danger flex-1" @click="logout">
              {{ $t('logout') }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
  <div ref="resetApiUrlModalEl" class="modal fade" tabindex="-1" aria-hidden="true">
    <div class="modal-dialog modal-dialog-centered">
      <div class="modal-content">
        <div class="modal-body">
          <h4 class="mb-16">{{ $t('reset-api-url-header') }}</h4>
          <p class="mb-32">
            {{ $t('reset-api-url-text') }}
          </p>
          <div class="d-flex flex-wrap gap-12">
            <button type="button" class="btn btn-outline-secondary flex-1" data-bs-dismiss="modal">
              {{ $t('cancel') }}
            </button>
            <button type="button" class="btn btn-danger flex-1" @click="resetApiUrl">
              {{ $t('continue') }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
